import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  createInventoryMovement,
  fetchInventory,
  fetchInventoryMovements,
  fetchInventorySummary,
  submitInventoryAdjustment,
  takeInventorySnapshot,
  takeInventorySnapshotFromProduction,
  type InventoryListMeta,
  type SubmitAdjustmentResponse,
} from './api';
import type {
  AdjustmentCreateInput,
  AdjustmentProposalInput,
  InventoryAggregateDto,
  InventoryMovementLogRow,
  InventorySummaryRow,
  MovementsFilter,
} from './types';

const KEY = 'inventory' as const;

// ---- legacy aggregated /inventory ------------------------------------------

export const useInventoryList = (
  batchId: string | undefined,
  params: { page?: number; per_page?: number } = {},
) => {
  return useQuery<{ data: InventoryAggregateDto[]; meta: InventoryListMeta }, ApiClientError>({
    queryKey: [KEY, 'list', batchId, params],
    queryFn: () => fetchInventory(batchId!, params),
    enabled: !!batchId,
    placeholderData: (prev) => prev,
  });
};

// ---- new: /inventory/summary -----------------------------------------------

export const useInventorySummary = (batchId: string | undefined) => {
  return useQuery<InventorySummaryRow[], ApiClientError>({
    queryKey: [KEY, 'summary', batchId],
    queryFn: () => fetchInventorySummary(batchId!),
    enabled: !!batchId,
    placeholderData: (prev) => prev,
  });
};

// ---- new: /inventory/movements ---------------------------------------------

export const useInventoryMovements = (
  batchId: string | undefined,
  filter: MovementsFilter = {},
) => {
  return useQuery<InventoryMovementLogRow[], ApiClientError>({
    queryKey: [KEY, 'movements', batchId, filter],
    queryFn: () => fetchInventoryMovements(batchId!, filter),
    enabled: !!batchId,
    placeholderData: (prev) => prev,
  });
};

// ---- new: submit adjustment proposal ---------------------------------------

export const useSubmitInventoryAdjustment = () => {
  const qc = useQueryClient();
  return useMutation<
    SubmitAdjustmentResponse,
    ApiClientError,
    { batchId: string; body: AdjustmentProposalInput }
  >({
    mutationFn: ({ batchId, body }) => submitInventoryAdjustment(batchId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

// ---- legacy adjustment-proposal compatibility + snapshot hooks ------------

export const useCreateInventoryMovement = () => {
  const qc = useQueryClient();
  return useMutation<
    SubmitAdjustmentResponse,
    ApiClientError,
    { batchId: string; body: AdjustmentCreateInput }
  >({
    mutationFn: ({ batchId, body }) => createInventoryMovement(batchId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useTakeInventorySnapshot = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (batchId) => takeInventorySnapshot(batchId),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useTakeInventorySnapshotFromProduction = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (batchId) => takeInventorySnapshotFromProduction(batchId),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};
